import {ChangeDetectionStrategy, Component, computed, input, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ResultatOppsett, SpørreundersøkelseSpørsmålKorellertResultatKomponent, SpørsmålOgResultater, UndersøkelseOgResultat} from "../spørreundersøkelse-spørsmål-korellert-resultat/spørreundersøkelse-spørsmål-korellert-resultat";
import {TranslateModule} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {NgTemplateOutlet} from "@angular/common";
import {MatButton} from "@angular/material/button";
import {KorellertGruppertSpørreundersøkelseResultatModell, SpørsmålModell, SpørsmålOgResultaterModell, SpørsmålsgruppeMedResultaterModell, Spørsmålstype} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {standard} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TypetNgTemplateDirektiv} from "web-ng2/delt/direktiver/typetNgTemplate/typetNgTemplate";
import {VisValgtittel} from "web-ng2/moduler/spørreundersøkelse/komponenter/rangering-resultater-søylediagram/rangering-resultater-søylediagram";

const debug = initDebugger('SpørreundersøkelseKorellertResultatlisteKomponent');

/**
 * Listevisning av korrelerte resultater for spørreundersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-korellert-resultatliste',
    templateUrl: 'spørreundersøkelse-korellert-resultatliste.html',
    styleUrl: 'spørreundersøkelse-korellert-resultatliste.scss',
    host: {'[class.viser-stedfortreder]': 'resultater() == null'},
    //sørger for at vis/skjul valgtittler i søylediagram er isoler til denne instansen (dvs endring i vis/skjul påvirker ikke visning av valgtitler globalt
    providers: [VisValgtittel],
    imports: [
        SpørreundersøkelseSpørsmålKorellertResultatKomponent,
        TranslateModule,
        MatButton,
        MatTooltip,
        NgTemplateOutlet,
        TypetNgTemplateDirektiv
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseKorellertResultatlisteKomponent {

    resultater = input<KorellertGruppertSpørreundersøkelseResultatModell | null>(null);
    tittel = input<string | null>(null);

    grupperinger = computed(() => standard(this.resultater(), korellertGruppertSpørreundersøkelseResultatModellMock).grupper
      .map(gruppe => new GruppertResultat(gruppe)));
    viserDetaljer = signal(false);

    /** Definerer typer for felter som er tilgjengelig i NgTemplate visSkjulInnslagFragment */
    visSkjulInnslagFragmentKontekstType!: { gruppe: GruppertResultat; };

    constructor() {
    }
}


export class GruppertResultat {
    /** En aggregert representasjon av spørsmålene i gruppen, der tilknyttet spørsmål alltid er av typen {@link Spørsmålstype#grupperingstittel} */
    readonly gruppe: SpørsmålOgResultaterMedMeta;
    /** Spørsmålene i denne gruppen */
    readonly innslag: SpørsmålOgResultaterMedMeta[] = [];
    /** Dersom sann vises alle spørsmålene */
    readonly viserInnslag = signal(false);

    constructor(private spørsmålsgruppeMedResultater: SpørsmålsgruppeMedResultaterModell) {
        this.gruppe = new SpørsmålOgResultaterMedMeta(spørsmålsgruppeMedResultater.gruppe);
        this.innslag = spørsmålsgruppeMedResultater.spoersmaalsamling.map(innslag => new SpørsmålOgResultaterMedMeta(innslag));
    }

    alleInnslagErAvSammeType(): boolean {
        return this.spørsmålsgruppeMedResultater.alleSpoersmaalErAvSammeTypeOgOppsett;
    }

    resultatOppsett(): ResultatOppsett {
        const oppsett: ResultatOppsett = {visualiseringstype: this.spørsmålsgruppeMedResultater.alleSpoersmaalErAvSammeTypeOgOppsett ?
              this.spørsmålsgruppeMedResultater.spoersmaalstype! : Spørsmålstype.grupperingstittel};
        if (this.viserInnslag()) return oppsett;
        if (this.gruppe.forrigeSpoersmaalHarSammeOppsett && !this.gruppe.nesteSpoersmaalHarSammeOppsett)
            return {...oppsett, valgtittelPlassering: 'under'};
        if (this.gruppe.nesteSpoersmaalHarSammeOppsett)
            return {...oppsett, visValgtittel: false};
        return oppsett;
    }

    visInnslag() {
        this.viserInnslag.set(true);
    }

    skjulInnslag() {
        this.viserInnslag.set(false);
    }
}

export class SpørsmålOgResultaterMedMeta implements SpørsmålOgResultater {

    constructor(private spoersmaalOgResultat: SpørsmålOgResultaterModell) {
    }

    get resultater(): UndersøkelseOgResultat[] {
        return this.spoersmaalOgResultat.resultater;
    }

    get spoersmaal(): SpørsmålModell {
        return this.spoersmaalOgResultat.spoersmaal;
    }

    get forrigeSpoersmaalHarSammeOppsett(): boolean {
        return this.spoersmaalOgResultat.forrigeSpoersmaalHarSammeOppsett;
    }

    get nesteSpoersmaalHarSammeOppsett(): boolean {
        return this.spoersmaalOgResultat.nesteSpoersmaalHarSammeOppsett;
    }

    resultatOppsett(): ResultatOppsett {
        const oppsett: ResultatOppsett = {visualiseringstype: this.spoersmaalOgResultat.spoersmaal.type, valgtittelPlassering: 'under', visValgtittel: true};
        /*
        //ved mange spørsmål i gruppen blir det vanskelig å holde rede hva valgtittelen er når den kun vises over første spørsmål i gruppen
        //viser derfor nå konsekvent valgtittelen under valgtalternativet
        if (!this.forrigeSpoersmaalHarSammeOppsett && this.nesteSpoersmaalHarSammeOppsett)
            return {...oppsett, valgtittelPlassering: 'over'};
        if (this.forrigeSpoersmaalHarSammeOppsett)
            return {...oppsett, visValgtittel: false};*/
        return oppsett;
    }
}


/** {@link KorellertGruppertSpørreundersøkelseResultatModell} populert med datasett egnet for midlertidig visning til reelle data er tilgjengelig */
const korellertGruppertSpørreundersøkelseResultatModellMock: KorellertGruppertSpørreundersøkelseResultatModell = {
    primaerUndersoekelse: {
        id: 'ua',
        eiendeGruppeId: 'a',
        tittel: 'x_ua',
        responsperiode: {start: '2006-12-02T23:00Z', slutt: '2006-12-03T09:15Z'},
        responsperiodeAntallDager: 1,
        erAapenForRespons: false,
        erAvsluttet: true,
        antallRespondenter: 1,
        antallRespondenterSvart: 1,
        kallendeBrukersRespondentId: null,
        skjemaId: '',
        skjemaTittel: ''
    },
    sekundaereUndersoekelser: [{
        id: 'ub',
        eiendeGruppeId: 'b',
        tittel: 'x_ub',
        responsperiode: {start: '2007-12-02T23:00Z', slutt: '2007-12-03T09:15Z'},
        responsperiodeAntallDager: 1,
        erAapenForRespons: false,
        erAvsluttet: true,
        antallRespondenter: 1,
        antallRespondenterSvart: 1,
        kallendeBrukersRespondentId: null,
        skjemaId: '',
        skjemaTittel: ''
    }],
    analyse: null,
    grupper: [
        {
            gruppe: {
                spoersmaal: {id: 'sa', tittel: 'x_sa-----------', beskrivelse: '', type: Spørsmålstype.grupperingstittel, oppsett: {}},
                resultater: [
                    {
                        undersoekelseTittel: 'xx_ua_tittel',
                        undersoekelseTittelKort: 'xx_ua',
                        undersoekelseId: 'ua',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 3, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    },
                    {
                        undersoekelseTittel: 'xx_ub_tittel',
                        undersoekelseTittelKort: 'xx_ub',
                        undersoekelseId: 'ub',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 5, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    }
                ],
                forrigeSpoersmaalHarSammeOppsett: false,
                nesteSpoersmaalHarSammeOppsett: true
            },
            spoersmaalsamling: [{
                spoersmaal: {id: 'sa_1', tittel: 'xsa_1------', beskrivelse: '', type: Spørsmålstype.rangering, oppsett: {rangering: {alternativer: [{tittel: 'a', verdi: 1, farge: '#eee'}, {tittel: 'b', verdi: 2, farge: '#eee'}, {tittel: 'c', verdi: 2, farge: '#eee'}]}}},
                resultater: [
                    {
                        undersoekelseTittel: 'xx_ua_tittel',
                        undersoekelseTittelKort: 'xx_ua',
                        undersoekelseId: 'ua',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    },
                    {
                        undersoekelseTittel: 'xx_ub_tittel',
                        undersoekelseTittelKort: 'xx_ub',
                        undersoekelseId: 'ub',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    }],
                forrigeSpoersmaalHarSammeOppsett: false,
                nesteSpoersmaalHarSammeOppsett: false
            }],
            alleSpoersmaalErAvSammeTypeOgOppsett: true,
            spoersmaalstype: Spørsmålstype.rangering,
            spoersmaalsoppsett: {rangering: {alternativer: [{tittel: 'a', verdi: 1, farge: '#eee'}, {tittel: 'b', verdi: 2, farge: '#eee'}, {tittel: 'c', verdi: 2, farge: '#eee'}]}}
        },
        {
            gruppe: {
                spoersmaal: {id: 'sb', tittel: 'x_sb', beskrivelse: '', type: Spørsmålstype.grupperingstittel, oppsett: {}},
                resultater: [
                    {
                        undersoekelseTittel: 'xx_ua_tittel',
                        undersoekelseTittelKort: 'xx_ua',
                        undersoekelseId: 'ua',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 2, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    },
                    {
                        undersoekelseTittel: 'xx_ub_tittel',
                        undersoekelseTittelKort: 'xx_ub',
                        undersoekelseId: 'ub',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 4, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 2, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    }
                ],
                forrigeSpoersmaalHarSammeOppsett: true,
                nesteSpoersmaalHarSammeOppsett: false
            },
            spoersmaalsamling: [{
                spoersmaal: {id: 'sb_1', tittel: 'xsb_1', beskrivelse: '', type: Spørsmålstype.rangering, oppsett: {rangering: {alternativer: [{tittel: 'a', verdi: 1, farge: '#eee'}, {tittel: 'b', verdi: 2, farge: '#eee'}, {tittel: 'c', verdi: 2, farge: '#eee'}]}}},
                resultater: [
                    {
                        undersoekelseTittel: 'xx_ua_tittel',
                        undersoekelseTittelKort: 'xx_ua',
                        undersoekelseId: 'ua',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    },
                    {
                        undersoekelseTittel: 'xx_ub_tittel',
                        undersoekelseTittelKort: 'xx_ub',
                        undersoekelseId: 'ub',
                        resultat: {
                            rangering: {
                                fordeling: [
                                    {alternativ: {tittel: 'a', verdi: 1, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'b', verdi: 2, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1},
                                    {alternativ: {tittel: 'c', verdi: 3, farge: '#eee'}, antall: 1, totaltAntallRespondenter: 1}
                                ],
                                vinnere: []
                            },
                            velgEnAvFlere: null,
                            velgNoenAvFlere: null,
                            boolsk: null,
                            fritekst: null
                        }
                    }],
                forrigeSpoersmaalHarSammeOppsett: false,
                nesteSpoersmaalHarSammeOppsett: false
            }],
            alleSpoersmaalErAvSammeTypeOgOppsett: true,
            spoersmaalstype: Spørsmålstype.rangering,
            spoersmaalsoppsett: {rangering: {alternativer: [{tittel: 'a', verdi: 1, farge: '#eee'}, {tittel: 'b', verdi: 2, farge: '#eee'}, {tittel: 'c', verdi: 2, farge: '#eee'}]}}
        }
    ]
}
